import {
  Company_User_Status,
  CompanyUserInvitesDocument,
  CompanyUsersDocument,
  CompanyUserStatisticDocument,
  useInitCompanyUserResetPasswordMutation,
  useInviteCompanyUserMutation,
  useRevokeCompanyUserInviteMutation,
  useToggleCompanyUserStatusMutation,
} from "@generated/graphql";
import {
  CloseOutlined,
  LockResetOutlined,
  MoreVertOutlined,
  PersonRemoveAlt1Outlined,
  RestartAltOutlined,
  SendOutlined,
} from "@mui/icons-material";
import { Divider, IconButton, ListItemIcon, MenuItem, MenuList, Popover, Typography } from "@mui/material";
import { ConfirmModalContext } from "context/confirmModalContext";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { parseApolloError } from "helpers/errorsParser";
import { MouseEvent, ReactNode, useContext, useState } from "react";
import { palette } from "theme";
import { TeamMember } from "./tableConfig/types";

type TeamMemberActionsProps = {
  employee: TeamMember;
  inviteToken?: string;
  isInvitesLoading: boolean;
};

type Action = {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  withDivider?: boolean;
};

const TeamMemberActions = ({ employee, inviteToken, isInvitesLoading }: TeamMemberActionsProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { addToast } = useContext(ToastContext);
  const { handleOpen: handleOpenConfirmModal } = useContext(ConfirmModalContext);
  const [initCompanyUserResetPassword, { loading: isResetPasswordLoading }] = useInitCompanyUserResetPasswordMutation();
  const [toggleCompanyUserStatus, { loading: isToggleStatusLoading }] = useToggleCompanyUserStatusMutation();
  const [inviteCompanyUser, { loading: isInviteLoading }] = useInviteCompanyUserMutation();
  const [revokeCompanyUserInvite, { loading: isRevokeInviteLoading }] = useRevokeCompanyUserInviteMutation();
  const isLoading = isResetPasswordLoading || isToggleStatusLoading || isInviteLoading || isRevokeInviteLoading;

  const handlePopoverOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => setAnchorEl(null);

  const handleResetPassword = () => {
    initCompanyUserResetPassword({
      variables: { companyUserId: employee.id },
    })
      .then(() => addToast({ type: ToastTypeEnum.SUCCESS, text: "Письмо для сброса пароля отправлено" }))
      .catch((error) => parseApolloError(error, { addToast }));
  };

  const handleToggleStatus = () => {
    toggleCompanyUserStatus({
      variables: {
        input: { companyUserId: employee.id },
      },
      refetchQueries: [CompanyUserStatisticDocument, CompanyUsersDocument],
      awaitRefetchQueries: true,
    })
      .then(() => addToast({ type: ToastTypeEnum.SUCCESS }))
      .catch((error) => parseApolloError(error, { addToast }));
  };

  const handleResendInvite = () => {
    inviteCompanyUser({
      variables: {
        input: {
          email: employee.userEmail.email,
          position: employee.position,
          role: employee.role,
        },
      },
      awaitRefetchQueries: true,
    })
      .then(() => addToast({ type: ToastTypeEnum.SUCCESS, text: "Приглашение отправлено повторно" }))
      .catch((error) => parseApolloError(error, { addToast }));
  };

  const handleRevokeInvite = () => {
    if (!inviteToken) return;

    revokeCompanyUserInvite({
      variables: { companyUserId: employee.id },
      refetchQueries: [CompanyUserInvitesDocument, CompanyUserStatisticDocument, CompanyUsersDocument],
      awaitRefetchQueries: true,
    })
      .then(() => addToast({ type: ToastTypeEnum.SUCCESS }))
      .catch((error) => parseApolloError(error, { addToast }));
  };

  const handleDisableAccess = () => {
    handleOpenConfirmModal({
      title: "Убрать доступ сотрудника?",
      description: "Сотрудник не сможет войти в аккаунт до восстановления доступа.",
      actionButtonText: "Убрать доступ",
      handleClick: handleToggleStatus,
    });
  };

  const handleRevokeInviteConfirm = () => {
    handleOpenConfirmModal({
      title: "Отозвать приглашение?",
      description: "Сотрудник не сможет воспользоваться текущим приглашением.",
      actionButtonText: "Отозвать",
      handleClick: handleRevokeInvite,
    });
  };

  let actions: Action[];

  if (employee.status === Company_User_Status.Pending) {
    actions = [
      {
        label: "Выслать повторно",
        icon: <SendOutlined fontSize='small' />,
        onClick: handleResendInvite,
      },
      {
        label: "Отозвать приглашение",
        icon: <CloseOutlined fontSize='small' />,
        onClick: handleRevokeInviteConfirm,
        danger: true,
        disabled: isInvitesLoading || !inviteToken,
      },
    ];
  } else if (employee.status === Company_User_Status.Deactivated) {
    actions = [
      {
        label: "Восстановить доступ",
        icon: <RestartAltOutlined fontSize='small' />,
        onClick: handleToggleStatus,
      },
    ];
  } else {
    actions = [
      {
        label: "Сбросить пароль",
        icon: <LockResetOutlined fontSize='small' />,
        onClick: handleResetPassword,
      },
      {
        label: "Убрать доступ",
        icon: <PersonRemoveAlt1Outlined fontSize='small' />,
        onClick: handleDisableAccess,
        danger: true,
        withDivider: true,
      },
    ];
  }

  return (
    <>
      <IconButton
        aria-label='Действия сотрудника'
        size='small'
        onClick={handlePopoverOpen}
        sx={{
          width: 30,
          height: 30,
          borderRadius: 1,
          color: "text.disabled",
          "&:hover": {
            backgroundColor: "bgSwitch",
            color: "text.primary",
          },
        }}>
        <MoreVertOutlined fontSize='small' />
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={handlePopoverClose}
        disableRestoreFocus
        slotProps={{
          paper: {
            sx: {
              mt: 0.25,
              minWidth: 190,
              p: 0.5,
              boxShadow: "0 12px 40px rgba(15, 23, 42, 0.14)",
              borderRadius: 2,
              border: "1px solid",
              borderColor: palette.border,
            },
          },
        }}>
        <MenuList disablePadding>
          {actions.map((action) => (
            <span key={action.label}>
              {action.withDivider && <Divider sx={{ my: 0.375, mx: 0.5 }} />}
              <MenuItem
                disabled={isLoading || action.disabled}
                onClick={(event) => {
                  event.stopPropagation();
                  handlePopoverClose();
                  action.onClick();
                }}
                sx={{
                  gap: 0,
                  alignItems: "center",
                  borderRadius: 0.75,
                  color: action.danger ? "error.main" : "text.primary",
                  "&:hover": {
                    backgroundColor: action.danger ? "error.light" : "bgSwitch",
                    color: action.danger ? "white" : "text.primary",
                  },
                  "&.Mui-disabled": {
                    color: action.danger ? "error.main" : "text.primary",
                    opacity: 0.4,
                  },
                }}>
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    color: "inherit",
                  }}>
                  {action.icon}
                </ListItemIcon>
                <Typography
                  variant='body14rg'
                  sx={{ lineHeight: "normal", fontWeight: 600, color: "unset" }}>
                  {action.label}
                </Typography>
              </MenuItem>
            </span>
          ))}
        </MenuList>
      </Popover>
    </>
  );
};

export default TeamMemberActions;
